import { useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { LayoutDashboard, CalendarDays, Users, MessageSquare, Quote, LogOut, Plus, Pencil, Trash2, Download } from "lucide-react";

function Sidebar() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const items = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/admin/classes", label: "Classes", icon: CalendarDays },
    { to: "/admin/registrations", label: "Registrations", icon: Users },
    { to: "/admin/inquiries", label: "Inquiries", icon: MessageSquare },
    { to: "/admin/testimonials", label: "Testimonials", icon: Quote },
  ];
  return (
    <aside className="w-64 bg-brand-navy text-white p-6 flex flex-col shrink-0 min-h-screen">
      <div className="font-display font-extrabold text-xl tracking-tight">KenMarq <span className="text-brand-amber">Admin</span></div>
      <nav className="mt-8 space-y-1 flex-1">
        {items.map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end}
            className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${isActive ? "bg-brand-teal text-white" : "text-white/70 hover:bg-white/10"}`}
            data-testid={`admin-nav-${it.label.toLowerCase()}`}>
            <it.icon className="h-4 w-4" /> {it.label}
          </NavLink>
        ))}
      </nav>
      <button onClick={async () => { await logout(); navigate("/admin/login"); }} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-white/70 hover:bg-white/10" data-testid="admin-logout">
        <LogOut className="h-4 w-4" /> Sign out
      </button>
    </aside>
  );
}

function Overview() {
  const [stats, setStats] = useState({});
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  const cards = [
    { label: "Upcoming Classes", value: stats.upcoming_classes || 0, color: "bg-brand-teal" },
    { label: "Total Registrations", value: stats.total_registrations || 0, color: "bg-brand-navy" },
    { label: "Waitlist Members", value: stats.waitlist_count || 0, color: "bg-brand-amber" },
    { label: "New Inquiries", value: stats.inquiries_new || 0, color: "bg-brand-coral" },
    { label: "Newsletter Subs", value: stats.newsletter_subs || 0, color: "bg-brand-navy" },
  ];
  return (
    <div>
      <h1 className="font-display text-3xl font-extrabold text-brand-navy tracking-tight">Dashboard</h1>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-6" data-testid="stats-grid">
        {cards.map((c) => (
          <Card key={c.label} className="p-6 border-none">
            <div className={`inline-block w-10 h-1 ${c.color} rounded-full mb-3`} />
            <div className="text-3xl font-display font-extrabold text-brand-navy">{c.value}</div>
            <div className="text-sm text-slate-500 mt-1">{c.label}</div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function ClassesAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { title: "", training_type: "", date: "", start_time: "", end_time: "", location: "KenMarq Training Center, Bethlehem, PA", description: "", price: 0, capacity: 12, registration_deadline: "", is_full: false };
  const [form, setForm] = useState(empty);

  const load = () => api.get("/admin/classes").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, price: Number(form.price), capacity: Number(form.capacity) };
      if (editing) await api.put(`/admin/classes/${editing}`, payload);
      else await api.post("/admin/classes", payload);
      toast.success("Saved");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this class?")) return;
    await api.delete(`/admin/classes/${id}`);
    load(); toast.success("Deleted");
  };

  const edit = (c) => { setEditing(c.id); setForm({ ...c }); setOpen(true); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-extrabold text-brand-navy tracking-tight">Classes</h1>
        <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-brand-coral hover:bg-brand-coral-dark rounded-full font-semibold btn-lift" data-testid="admin-add-class"><Plus className="h-4 w-4 mr-1" /> Add Class</Button>
      </div>
      <div className="mt-6 bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-surface text-brand-navy">
            <tr>
              <th className="text-left p-4 font-semibold">Title</th>
              <th className="text-left p-4 font-semibold">Date</th>
              <th className="text-left p-4 font-semibold">Seats</th>
              <th className="text-left p-4 font-semibold">Price</th>
              <th className="text-right p-4 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="admin-classes-table">
            {items.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="p-4 font-semibold text-brand-navy">{c.title}<div className="text-xs text-slate-500 font-normal">{c.training_type}</div></td>
                <td className="p-4">{new Date(c.date).toDateString()}<div className="text-xs text-slate-500">{c.start_time}–{c.end_time}</div></td>
                <td className="p-4">{c.seats_taken}/{c.capacity}{c.is_full && <Badge className="ml-2 bg-brand-coral">Full</Badge>}</td>
                <td className="p-4">${c.price}</td>
                <td className="p-4 text-right">
                  <Button size="sm" variant="ghost" onClick={() => edit(c)} data-testid={`edit-class-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(c.id)} data-testid={`delete-class-${c.id}`}><Trash2 className="h-4 w-4 text-brand-coral" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-brand-navy">{editing ? "Edit Class" : "New Class"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <FF label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="cf-title" required />
              <FF label="Training type" value={form.training_type} onChange={(v) => setForm({ ...form, training_type: v })} testid="cf-type" required />
              <FF label="Date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="cf-date" required />
              <FF label="Registration deadline" type="date" value={form.registration_deadline} onChange={(v) => setForm({ ...form, registration_deadline: v })} testid="cf-deadline" required />
              <FF label="Start time" type="time" value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} testid="cf-start" required />
              <FF label="End time" type="time" value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} testid="cf-end" required />
              <FF label="Price ($)" type="number" step="0.01" value={form.price} onChange={(v) => setForm({ ...form, price: v })} testid="cf-price" required />
              <FF label="Capacity" type="number" value={form.capacity} onChange={(v) => setForm({ ...form, capacity: v })} testid="cf-capacity" required />
              <div className="md:col-span-2"><FF label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="cf-loc" required /></div>
            </div>
            <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cf-desc" /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_full} onChange={(e) => setForm({ ...form, is_full: e.target.checked })} data-testid="cf-full" /> Mark as full (waitlist only)</label>
            <DialogFooter><Button type="submit" className="bg-brand-coral hover:bg-brand-coral-dark rounded-full" data-testid="cf-save">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FF({ label, value, onChange, type = "text", testid, required, step }) {
  return (
    <div>
      <Label className="text-brand-navy font-semibold text-sm">{label}</Label>
      <Input type={type} step={step} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 bg-brand-surface border-slate-200 rounded-lg" data-testid={testid} />
    </div>
  );
}

function exportCSV(rows, filename) {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => JSON.stringify(r[k] ?? "")).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
}

function Registrations() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/registrations").then((r) => setItems(r.data)); }, []);
  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-extrabold text-brand-navy tracking-tight">Registrations</h1>
        <Button onClick={() => exportCSV(items, "registrations.csv")} variant="outline" className="rounded-full font-semibold" data-testid="export-registrations"><Download className="h-4 w-4 mr-1" /> Export CSV</Button>
      </div>
      <div className="mt-6 bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-surface text-brand-navy"><tr>
            <th className="text-left p-4 font-semibold">Participant</th>
            <th className="text-left p-4 font-semibold">Class</th>
            <th className="text-left p-4 font-semibold">Contact</th>
            <th className="text-left p-4 font-semibold">Seats</th>
            <th className="text-left p-4 font-semibold">Status</th>
          </tr></thead>
          <tbody data-testid="admin-registrations-table">
            {items.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="p-4 font-semibold text-brand-navy">{r.full_name}<div className="text-xs text-slate-500 font-normal">{r.organization}</div></td>
                <td className="p-4">{r.class_title}<div className="text-xs text-slate-500">{new Date(r.class_date).toDateString()}</div></td>
                <td className="p-4 text-xs">{r.email}<br />{r.phone}</td>
                <td className="p-4">{r.seats}</td>
                <td className="p-4"><Badge className={r.status === "waitlist" ? "bg-brand-amber" : "bg-brand-teal"}>{r.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="p-8 text-center text-slate-500">No registrations yet.</div>}
      </div>
    </div>
  );
}

function Inquiries() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/inquiries").then((r) => setItems(r.data)); }, []);
  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-extrabold text-brand-navy tracking-tight">Group Training Inquiries</h1>
        <Button onClick={() => exportCSV(items, "inquiries.csv")} variant="outline" className="rounded-full font-semibold" data-testid="export-inquiries"><Download className="h-4 w-4 mr-1" /> Export CSV</Button>
      </div>
      <div className="mt-6 space-y-4" data-testid="admin-inquiries-list">
        {items.map((i) => (
          <Card key={i.id} className="p-6 border border-slate-200">
            <div className="flex flex-wrap justify-between gap-3">
              <div>
                <div className="font-display font-bold text-brand-navy text-lg">{i.first_name} {i.last_name} {i.organization && <span className="text-slate-500 font-normal">· {i.organization}</span>}</div>
                <div className="text-sm text-slate-600 mt-1">{i.email} · {i.phone}</div>
              </div>
              <Badge className="bg-brand-teal">{i.registrant_type}</Badge>
            </div>
            <div className="grid md:grid-cols-3 gap-3 mt-4 text-sm">
              <div><span className="font-semibold text-brand-navy">Training:</span> {i.training_requested}</div>
              <div><span className="font-semibold text-brand-navy">Participants:</span> {i.participants}</div>
              <div><span className="font-semibold text-brand-navy">Format:</span> {i.preferred_format}</div>
              <div><span className="font-semibold text-brand-navy">Preferred date:</span> {i.preferred_date || "—"}</div>
              <div><span className="font-semibold text-brand-navy">Location:</span> {i.city_state || "—"}</div>
              <div><span className="font-semibold text-brand-navy">Timeline:</span> {i.how_soon || "—"}</div>
            </div>
            {i.additional_notes && <p className="mt-3 p-3 bg-brand-surface rounded-lg text-sm text-slate-700">{i.additional_notes}</p>}
          </Card>
        ))}
        {items.length === 0 && <div className="p-8 text-center text-slate-500 bg-white rounded-2xl border border-slate-200">No inquiries yet.</div>}
      </div>
    </div>
  );
}

function Testimonials() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ author: "", role: "", quote: "", rating: 5, is_published: true });
  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/testimonials", form); setForm({ author: "", role: "", quote: "", rating: 5, is_published: true }); load(); toast.success("Added"); }
    catch (err) { toast.error(formatError(err)); }
  };
  const del = async (id) => { await api.delete(`/admin/testimonials/${id}`); load(); };
  return (
    <div>
      <h1 className="font-display text-3xl font-extrabold text-brand-navy tracking-tight">Testimonials</h1>
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <form onSubmit={save} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
          <h2 className="font-display font-bold text-brand-navy">Add new</h2>
          <FF label="Author" value={form.author} onChange={(v) => setForm({ ...form, author: v })} testid="tf-author" required />
          <FF label="Role / Organization" value={form.role} onChange={(v) => setForm({ ...form, role: v })} testid="tf-role" />
          <div><Label>Quote</Label><Textarea rows={3} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} required data-testid="tf-quote" /></div>
          <Button type="submit" className="bg-brand-coral hover:bg-brand-coral-dark rounded-full" data-testid="tf-add">Add</Button>
        </form>
        <div className="space-y-3" data-testid="admin-testimonials-list">
          {items.map((t) => (
            <Card key={t.id} className="p-5 border border-slate-200">
              <div className="flex justify-between">
                <div>
                  <div className="font-display font-bold text-brand-navy">{t.author}</div>
                  <div className="text-xs text-slate-500">{t.role}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => del(t.id)} data-testid={`delete-testimonial-${t.id}`}><Trash2 className="h-4 w-4 text-brand-coral" /></Button>
              </div>
              <p className="mt-2 text-sm text-slate-700">"{t.quote}"</p>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  return (
    <div className="flex bg-brand-surface min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 overflow-x-auto">
        <Routes>
          <Route index element={<Overview />} />
          <Route path="classes" element={<ClassesAdmin />} />
          <Route path="registrations" element={<Registrations />} />
          <Route path="inquiries" element={<Inquiries />} />
          <Route path="testimonials" element={<Testimonials />} />
        </Routes>
      </div>
    </div>
  );
}
